import React from 'react';
import { ShieldCheckIcon, DocumentTextIcon, UserIcon, LockClosedIcon } from '@heroicons/react/24/outline';

const Privacy: React.FC = () => {
  const sections = [
    {
      title: 'Information We Collect',
      icon: DocumentTextIcon,
      content: [
        {
          subtitle: 'Personal Information',
          details: [
            'Name, email address, phone number, and postal address',
            'Date of birth and passport information for travel arrangements',
            'Dietary requirements, medical conditions, and special needs',
            'Emergency contact information'
          ]
        },
        {
          subtitle: 'Travel Information',
          details: [
            'Safari preferences and interests',
            'Travel dates and group size',
            'Accommodation preferences',
            'Payment and billing information'
          ]
        },
        {
          subtitle: 'Technical Information',
          details: [
            'IP address and browser information',
            'Website usage data and analytics',
            'Cookies and similar tracking technologies',
            'Device information and location data'
          ]
        }
      ]
    },
    {
      title: 'How We Use Your Information',
      icon: UserIcon,
      content: [
        {
          subtitle: 'Service Delivery',
          details: [
            'Process and manage your safari bookings',
            'Provide customer support and assistance',
            'Send booking confirmations and travel documents',
            'Coordinate with local guides and accommodations'
          ]
        },
        {
          subtitle: 'Communication',
          details: [
            'Send important updates about your safari',
            'Provide travel tips and preparation information',
            'Share promotional offers and new tour announcements',
            'Respond to your inquiries and feedback'
          ]
        },
        {
          subtitle: 'Improvement',
          details: [
            'Analyze website usage to improve our services',
            'Conduct customer satisfaction surveys',
            'Develop new tours and experiences',
            'Enhance our website and booking system'
          ]
        }
      ]
    },
    {
      title: 'Information Sharing',
      icon: LockClosedIcon,
      content: [
        {
          subtitle: 'Service Providers',
          details: [
            'Local safari guides and tour operators',
            'Accommodation providers and transportation services',
            'Payment processors and financial institutions',
            'Travel insurance providers'
          ]
        },
        {
          subtitle: 'Legal Requirements',
          details: [
            'Comply with applicable laws and regulations',
            'Respond to legal requests and court orders',
            'Protect our rights and prevent fraud',
            'Ensure safety and security of our services'
          ]
        },
        {
          subtitle: 'Business Transfers',
          details: [
            'In case of merger, acquisition, or sale of assets',
            'With your explicit consent',
            'To protect our legitimate business interests',
            'As required by law or regulation'
          ]
        }
      ]
    }
  ];

  const rights = [
    {
      title: 'Access Your Data',
      description: 'Request a copy of all personal information we hold about you'
    },
    {
      title: 'Correct Information',
      description: 'Update or correct any inaccurate personal information'
    },
    {
      title: 'Delete Your Data',
      description: 'Request deletion of your personal information (subject to legal requirements)'
    },
    {
      title: 'Data Portability',
      description: 'Receive your data in a structured, machine-readable format'
    },
    {
      title: 'Opt-Out',
      description: 'Unsubscribe from marketing communications at any time'
    },
    {
      title: 'Restrict Processing',
      description: 'Request limitation of how we process your personal information'
    }
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero Section */}
      <div className="bg-primary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-xl opacity-90 max-w-3xl mx-auto">
            Your privacy is important to us. Learn how we collect, use, and protect your personal information.
          </p>
          <div className="mt-6 text-sm opacity-75">
            Last updated: January 15, 2024
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Privacy Image Banner */}
        <div className="relative rounded-lg overflow-hidden shadow-sm mb-12">
          <img
            src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
            alt="Data privacy and security"
            className="w-full h-48 object-cover"
          />
          <div className="absolute inset-0 bg-primary-900/70 flex items-center justify-center">
            <div className="text-center text-white px-4">
              <h2 className="text-2xl font-bold mb-2">Your Privacy Matters</h2>
              <p className="text-lg">We protect your personal information with industry-leading security</p>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="bg-white rounded-lg shadow-sm p-8 border border-neutral-200 mb-8">
          <div className="flex items-start">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
              <ShieldCheckIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-primary-900 mb-4">Our Commitment to Privacy</h2>
              <p className="text-neutral-600 leading-relaxed">
                Maasai SightTrails Safaris is committed to protecting your privacy and personal information.
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information
                when you visit our website or use our services. By using our services, you consent to the
                data practices described in this policy.
              </p>
            </div>
          </div>
        </div>

        {/* Main Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-neutral-200">
              <div className="p-6 border-b border-neutral-200">
                <div className="flex items-center">
                  <section.icon className="h-6 w-6 text-primary-600 mr-3" />
                  <h2 className="text-2xl font-bold text-primary-900">{section.title}</h2>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-6">
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex}>
                      <h3 className="text-lg font-semibold text-primary-900 mb-3">{item.subtitle}</h3>
                      <ul className="space-y-2">
                        {item.details.map((detail, detailIndex) => (
                          <li key={detailIndex} className="flex items-start text-neutral-700">
                            <div className="w-2 h-2 bg-primary-600 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Your Rights */}
        <div className="bg-white rounded-lg shadow-sm p-8 border border-neutral-200 mt-8">
          <h2 className="text-2xl font-bold text-primary-900 mb-6">Your Privacy Rights</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {rights.map((right, index) => (
              <div key={index} className="flex items-start">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <div className="w-3 h-3 bg-primary-600 rounded-full"></div>
                </div>
                <div>
                  <h3 className="font-semibold text-primary-900 mb-1">{right.title}</h3>
                  <p className="text-neutral-600 text-sm">{right.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Data Security */}
        <div className="bg-white rounded-lg shadow-sm p-8 border border-neutral-200 mt-8">
          <h2 className="text-2xl font-bold text-primary-900 mb-4">Data Security</h2>
          <p className="text-neutral-600 mb-4">
            We implement appropriate technical and organizational measures to protect your personal information
            against unauthorized access, alteration, disclosure, or destruction. These measures include:
          </p>
          <ul className="space-y-2 text-neutral-700">
            <li className="flex items-start">
              <div className="w-2 h-2 bg-primary-600 rounded-full mr-3 mt-2 flex-shrink-0"></div>
              SSL encryption for all data transmission
            </li>
            <li className="flex items-start">
              <div className="w-2 h-2 bg-primary-600 rounded-full mr-3 mt-2 flex-shrink-0"></div>
              Secure servers and regular security updates
            </li>
            <li className="flex items-start">
              <div className="w-2 h-2 bg-primary-600 rounded-full mr-3 mt-2 flex-shrink-0"></div>
              Limited access to personal information on a need-to-know basis
            </li>
            <li className="flex items-start">
              <div className="w-2 h-2 bg-primary-600 rounded-full mr-3 mt-2 flex-shrink-0"></div>
              Regular security audits and assessments
            </li>
          </ul>
        </div>

        {/* Contact Information */}
        <div className="bg-primary-50 rounded-lg p-8 mt-8">
          <h2 className="text-2xl font-bold text-primary-900 mb-4">Contact Us About Privacy</h2>
          <p className="text-neutral-600 mb-4">
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-primary-900 mb-2">Privacy Officer</h3>
              <p className="text-neutral-700">Sarah Mwangi</p>
              <p className="text-neutral-700">sighttrailsafaris@gmail.com</p>
              <p className="text-neutral-700">+254 708 560 665</p>
            </div>
            <div>
              <h3 className="font-semibold text-primary-900 mb-2">Mailing Address</h3>
              <p className="text-neutral-700">
                Maasai SightTrails Safaris<br />
                P.O. Box 12345<br />
                Nairobi, Kenya
              </p>
            </div>
          </div>
        </div>

        {/* Updates */}
        <div className="bg-neutral-100 rounded-lg p-6 mt-8">
          <h3 className="text-lg font-semibold text-primary-900 mb-2">Policy Updates</h3>
          <p className="text-neutral-600 text-sm">
            We may update this Privacy Policy from time to time. We will notify you of any changes by
            posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage
            you to review this Privacy Policy periodically for any changes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
